import { Link } from 'react-router-dom';
import Card from '../components/Card.jsx';
import Button from '../components/Button.jsx';
import { planCardClassName, planFeatures, TIER_PLANS, universeCountLabel } from '../modules/plans.js';
import useUniverseSummary from '../hooks/useUniverseSummary.js';
import './auth.css';

// The real MyStocks Africa "peak" mark (brand/Brand Assets/Favicon/SVG/
// Favicon Light.svg per BRAND_TOKENS.md §6) — inlined, not redrawn. Used
// both in the public nav wordmark accent and the "Powered by" footer
// lockup.
function MsaMark({ className }) {
  return (
    <svg className={className} viewBox="0 0 90 90" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <polygon fill="#057eff" points="10.2 50.91 32.61 29.4 41.17 37.62 18.7 60.05 10.2 50.91" />
      <polygon fill="#011435" points="35.53 51.27 57.16 29.4 79.8 50.8 70.79 60.6 57.45 46.86 44.21 60.6 35.53 51.27" />
    </svg>
  );
}

const MODULES = [
  {
    num: '01',
    title: 'Daily Briefings',
    desc: 'An editorial market note, index strip, top movers, and a corporate-action calendar — every morning, before the open.',
  },
  {
    num: '02',
    title: 'Stock Screener',
    desc: 'Fundamentals filters over MyStocks Africa data across six exchanges — NGX, NSE-Kenya, JSE, GSE, EGX, BRVM.',
  },
  {
    num: '03',
    title: 'Portfolio & Backtest',
    desc: 'Construct a portfolio, run institutional risk analytics, and backtest strategies on split/bonus/dividend-adjusted prices.',
  },
  {
    num: '04',
    title: 'Research Marketplace',
    desc: 'Publish and discover research ideas, with a verified track-record ledger. Strategy copier is a Phase 2 add-on.',
  },
];

export default function Landing() {
  const universe = useUniverseSummary();
  const universeLabel = universeCountLabel(universe?.total);
  return (
    <div className="aq-public">
      <header className="aq-public-nav">
        <Link to="/" className="aq-public-brand">
          <span className="aq-wordmark">
            Alpha<span className="aq-wordmark-accent">Qap</span>
          </span>
        </Link>
        <nav className="aq-public-nav-actions">
          <Button as={Link} to="/login" variant="ghost" size="sm">
            Log in
          </Button>
          <Button as={Link} to="/signup" variant="primary" size="sm">
            Sign up
          </Button>
        </nav>
      </header>

      <main className="aq-public-main">
        <section className="aq-landing-hero">
          <div className="aq-landing-eyebrow">Qaptal × AlphaQuark · co-branded platform</div>
          <h1>
            Professional portfolio intelligence,
            <br />
            built for <span className="aq-landing-accent">African markets</span>.
          </h1>
          <p>
            AlphaQap is a decision-grade workspace for African asset managers and advisers: live
            market data and fundamentals, a screening and construction workspace, institutional
            risk analytics, and a proven backtesting engine — all in one dashboard. No live
            trading, no custody. Just analysis you can act on.
          </p>
          <div className="aq-landing-cta">
            <Button as={Link} to="/signup" variant="primary">
              Create your account →
            </Button>
            <Button as={Link} to="/login" variant="secondary">
              I already have an account
            </Button>
          </div>
        </section>

        <section>
          <h2 className="aq-section-title heading">The workspace</h2>
          <p className="aq-module-desc aq-landing-lead">
            Everything in one place: <strong>brief</strong> the market each morning,{' '}
            <strong>screen</strong> the {universeLabel} African universe for opportunities,{' '}
            <strong>construct</strong> and <strong>stress-test</strong> portfolios against a
            real backtesting engine, and <strong>publish</strong> your research — with a
            verified track record behind it.
          </p>
          <div className="aq-grid aq-grid--4">
            {MODULES.map((m) => (
              <Card key={m.num}>
                <span className="aq-landing-module-num num">{m.num}</span>
                <h3 className="heading" style={{ fontSize: 16, marginBottom: 8 }}>
                  {m.title}
                </h3>
                <p className="aq-module-desc">{m.desc}</p>
              </Card>
            ))}
          </div>
        </section>

        <section>
          <div className="aq-row-head">
            <h2 className="aq-section-title heading">Plans that fit how you work</h2>
            <span className="aq-module-desc">
              Start free on PAYG. Upgrade when you want the credit included and cheaper runs.
            </span>
          </div>
          <div className="aq-grid aq-grid--3">
            {TIER_PLANS.map((plan) => (
              <Card
                key={plan.id}
                className={planCardClassName(plan.popular)}
              >
                {plan.popular && <span className="aq-plan-badge">Most popular</span>}
                <h3 className="heading" style={{ fontSize: 18 }}>
                  {plan.name}
                </h3>
                <p className="aq-plan-price num">{plan.price}</p>
                <p className="aq-module-desc">{plan.blurb}</p>
                <ul className="aq-plan-list">
                  {planFeatures(plan, universe?.total).map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                <div style={{ marginTop: 'auto', paddingTop: 16 }}>
                  <Button as={Link} to="/signup" variant={plan.popular ? 'primary' : 'secondary'} size="sm">
                    {plan.cta || 'Get started'}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </section>

        <section>
          <Card>
            <div className="aq-powered-by">
              <MsaMark className="aq-powered-by-mark" />
              Powered by <b>MyStocks Africa</b> market data, across six African exchanges.
            </div>
          </Card>
        </section>
      </main>

      <footer className="aq-public-footer">
        <div className="aq-powered-by">
          <MsaMark className="aq-powered-by-mark" />
          <span>
            <b>AlphaQap</b> — co-branded platform for Qaptal Research × AlphaQuark.
          </span>
        </div>
        <p className="aq-disclaimer">
          Figures shown across this product are <b>illustrative demo data</b> for presentation,
          not live market data or investment advice. Scope: daily briefings, stock screener,
          portfolio construction &amp; backtesting, and the research/idea marketplace, on a
          co-branded foundation with split/bonus/dividend-adjusted price history. AlphaQuark
          provides the technology layer only — no investment advice, no custody, no execution.
        </p>
      </footer>
    </div>
  );
}
